import { initializeApp } from 'firebase/app'
import {
  getAuth,
  GoogleAuthProvider,
  signInAnonymously as firebaseSignInAnonymously,
  signInWithPopup,
  signInWithCredential,
  linkWithPopup,
  getAdditionalUserInfo,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  type AuthError,
  type UserCredential,
  type User,
} from 'firebase/auth'

// New-account creation is disabled by default — sign-up would just leave a real account with no
// team, immediately caught by useBootstrap's "not an admin of any team" kickout, but with an
// orphaned Firebase user left behind by then. Bypassed via a confirmed valid admin-invite link
// (see signInWithGoogle's `allowNewAccount` param and LoginPage — ADM-572/BAC-1739) or via the
// front-door signup flow (SignupPage — ADM-616), which is responsible for its own team creation
// immediately afterward.
export class SignUpDisabledError extends Error {
  code = 'auth/sign-up-disabled'
}

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
}

const app = initializeApp(firebaseConfig)
export const auth = getAuth(app)

const googleProvider = new GoogleAuthProvider()

// Tracked at module scope so a real sign-in started while this is still in flight can wait for
// it to fully settle first — see waitForAnonymousBootstrap below. Without this, the mount-time
// call in LoginPage (fire-and-forget) can resolve *after* a fast real sign-in already succeeded
// (e.g. a password manager submitting instantly), silently overwriting the real session back to
// anonymous when it lands, since Firebase has no way to know a real sign-in already won.
let anonymousSignInPromise: Promise<User> | null = null

// Gives signed-out screens (e.g. the login page, before any real sign-in) a valid Firebase ID
// token to call the backend with — the backend requires *some* valid token on every query, it
// doesn't care whether the underlying user is anonymous. Real sign-in/sign-up below upgrades or
// discards this session; never leave it dangling as a separate abandoned account.
export async function ensureAnonymousSignIn() {
  if (auth.currentUser) return auth.currentUser
  if (!anonymousSignInPromise) {
    anonymousSignInPromise = firebaseSignInAnonymously(auth).then((r) => r.user)
  }
  return anonymousSignInPromise
}

// Call at the start of every real sign-in path. Joins the same in-flight promise as
// ensureAnonymousSignIn (if one is running) so the anonymous bootstrap is always fully settled
// before a real sign-in begins — no anonymous response left in flight to land afterward and
// clobber it.
async function waitForAnonymousBootstrap() {
  if (anonymousSignInPromise) await anonymousSignInPromise.catch(() => {})
}

// Deletes a since-abandoned anonymous session. Best-effort — if it fails (e.g. the token aged
// out), it's an orphaned anonymous row, not a broken sign-in, so we swallow the error rather
// than surface it to the user who just successfully authenticated.
//
// MUST run before establishing the real session it's replacing, never after. Firebase's JS SDK
// resets auth.currentUser to null as part of user.delete() whenever that user object was ever
// the Auth instance's current user — even if a different (real) user has since replaced it in
// auth.currentUser. Calling this after the real sign-in silently clobbers that fresh session
// back to signed-out, with no error surfaced anywhere (confirmed via logging: auth.currentUser
// held the new real uid right after signInWithEmailAndPassword resolved, then flipped to null
// immediately after this ran). Deleting first, while the anonymous user is still the actual
// current user, avoids the collision entirely — there's no real session yet for it to touch.
async function cleanUpAbandonedAnonymousUser(user: User) {
  await user.delete().catch(() => {})
}

// `allowNewAccount` is passed `true` from two places: LoginPage, after it's confirmed a valid,
// unexpired, unrevoked admin-invite code via getTeamAdminInviteInfo (BAC-1739), and SignupPage's
// front-door flow (ADM-616). Every other caller (normal sign-in) leaves it `false`, so
// sign-up-disabled stays in force by default.
//
// Returns isNewAccount alongside the credential (ADM-703) — a caller that unconditionally runs
// new-account setup (creating a brand, starting a trial, etc.) after this resolves would do that
// to an *existing* user's account too, since a successful Google sign-in looks the same either
// way from the caller's side. SignupPage is the one place that matters today.
export async function signInWithGoogle(allowNewAccount = false) {
  await waitForAnonymousBootstrap()
  const currentUser = auth.currentUser
  let result: UserCredential
  // Whether this Google identity had zero prior Firebase presence — tracked by which branch
  // actually ran rather than trusted from `additionalUserInfo.isNewUser`, since that flag's
  // semantics for a *link* (vs. a top-level sign-in) aren't reliably documented.
  let isNewAccount: boolean

  if (currentUser?.isAnonymous) {
    try {
      result = await linkWithPopup(currentUser, googleProvider)
      isNewAccount = true // linking only succeeds if this Google identity was never seen before
    } catch (e: unknown) {
      const code = (e as AuthError).code
      if (code !== 'auth/credential-already-in-use') throw e

      // The anonymous user tried to link a Google account that already exists elsewhere.
      // Reuse the credential from the error to sign into the real account without a second
      // popup — clean up the now-abandoned anonymous session first (see the function's own
      // comment for why the order matters), then sign in for real.
      const credential = GoogleAuthProvider.credentialFromError(e as AuthError)
      await cleanUpAbandonedAnonymousUser(currentUser)
      result = credential ? await signInWithCredential(auth, credential) : await signInWithPopup(auth, googleProvider)
      isNewAccount = false // the "already in use" error is itself proof this account pre-existed
    }
  } else {
    // Shouldn't normally happen — LoginPage ensures an anonymous session on mount — but cover
    // it defensively (e.g. a race before that effect resolves).
    result = await signInWithPopup(auth, googleProvider)
    isNewAccount = !!getAdditionalUserInfo(result)?.isNewUser
  }

  if (isNewAccount && !allowNewAccount) {
    await result.user.delete().catch(() => {})
    throw new SignUpDisabledError()
  }

  return { result, isNewAccount }
}

export async function signInWithEmail(email: string, password: string) {
  await waitForAnonymousBootstrap()
  const currentUser = auth.currentUser
  if (currentUser?.isAnonymous) await cleanUpAbandonedAnonymousUser(currentUser)
  return signInWithEmailAndPassword(auth, email, password)
}

// Called from LoginPage's invite-redemption path and SignupPage's front-door flow (see
// signInWithGoogle's comment above) — general email/password sign-up stays unavailable
// everywhere else in the app.
export async function signUpWithEmail(email: string, password: string) {
  await waitForAnonymousBootstrap()
  const currentUser = auth.currentUser
  if (currentUser?.isAnonymous) await cleanUpAbandonedAnonymousUser(currentUser)
  return createUserWithEmailAndPassword(auth, email, password)
}

export async function signOut() {
  return firebaseSignOut(auth)
}

export async function getIdToken(user: User, forceRefresh = false) {
  return user.getIdToken(forceRefresh)
}

export { onAuthStateChanged }
export type { User }
